import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Space,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalApi } from '@Apis';
import type { HpuThresholdSettings } from '@Apis/global';
import CSpinner from '@Components/CSpinner';

export enum ThresholdOption {
  BuiltIn = 0,
  Customize = 1,
}

export enum ThresholdLevel {
  Normal = 0,
  Aggressive = 1,
  Conservative = 2,
}

interface DetectionThresholdModalProps {
  visible: boolean;
  onClose: () => void;
  disabled?: boolean;
}

export const DetectionThresholdModal: React.FC<
  DetectionThresholdModalProps
> = ({ visible, onClose, disabled = false }) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [thresholdOption, setThresholdOption] = useState<ThresholdOption>(
    ThresholdOption.BuiltIn
  );
  const [thresholdLevel, setThresholdLevel] = useState<ThresholdLevel>(
    ThresholdLevel.Normal
  );
  const [initialSettings, setInitialSettings] =
    useState<HpuThresholdSettings | null>(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await GlobalApi.getHpuThresholdSettings();
      const settings = response.data;
      setThresholdOption(settings.hpuThresholdOption);
      setThresholdLevel(settings.hpuThresholdLevel);
      setInitialSettings(settings);
    } catch (error) {
      console.error('Failed to load HPU threshold settings:', error);
      setThresholdOption(ThresholdOption.BuiltIn);
      setThresholdLevel(ThresholdLevel.Normal);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      loadSettings().then();
    }
  }, [visible]);

  const handleOptionChange = (value: string) => {
    const option = Number(value) as ThresholdOption;
    setThresholdOption(option);
    if (option === ThresholdOption.Customize) {
      setThresholdLevel(ThresholdLevel.Normal);
    }
  };

  const handleLevelChange = (value: string) => {
    setThresholdLevel(Number(value) as ThresholdLevel);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await GlobalApi.updateHpuThresholdSettings({
        hpuThresholdOption: thresholdOption,
        hpuThresholdLevel:
          thresholdOption === ThresholdOption.Customize
            ? thresholdLevel
            : ThresholdLevel.Normal,
      });
      window.showNotification({
        titleMsg: t(
          'globalSetting.highProfileUsers.detectionThreshold.save.success'
        ),
        severity: 'success',
      });
      onClose();
    } catch (error) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (initialSettings) {
      setThresholdOption(initialSettings.hpuThresholdOption);
      setThresholdLevel(initialSettings.hpuThresholdLevel);
    }
    onClose();
  };

  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      isClosable
      isOpen={visible}
      onClose={handleCancel}
      size="md"
      width="672"
      height="484"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('globalSetting.highProfileUsers.detectionThreshold.modal.title')}
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <Flex justifyContent="center" py="4x">
              <CSpinner />
            </Flex>
          ) : (
            <Stack direction="column" spacing="4x">
              <Box>
                <Text
                  fontSize="sm"
                  lineHeight="sm"
                  color={colorStyle.color.primary}
                  mb="2x"
                >
                  {t(
                    'globalSetting.highProfileUsers.detectionThreshold.modal.description'
                  )}
                </Text>
                <Text
                  fontSize="sm"
                  lineHeight="sm"
                  color={colorStyle.color.secondary}
                >
                  <Box as="span" fontWeight="700" mr="1x">
                    {t('globalSetting.note.title')}
                  </Box>
                  {t(
                    'globalSetting.highProfileUsers.detectionThreshold.modal.note'
                  )}
                </Text>
              </Box>

              <Box>
                <RadioGroup
                  value={String(thresholdOption)}
                  onChange={handleOptionChange}
                >
                  <Stack direction="column" spacing="2x">
                    <Radio value={String(ThresholdOption.BuiltIn)}>
                      {t(
                        'globalSetting.highProfileUsers.detectionThreshold.option.builtIn'
                      )}
                    </Radio>
                    <Radio value={String(ThresholdOption.Customize)}>
                      {t(
                        'globalSetting.highProfileUsers.detectionThreshold.option.customize'
                      )}
                    </Radio>
                  </Stack>
                </RadioGroup>

                {thresholdOption === ThresholdOption.Customize && (
                  <Box ml="6x" mt="2x">
                    <RadioGroup
                      value={String(thresholdLevel)}
                      onChange={handleLevelChange}
                    >
                      <Stack direction="column" spacing="2x">
                        <Box>
                          <Radio value={String(ThresholdLevel.Aggressive)}>
                            {t(
                              'globalSetting.highProfileUsers.detectionThreshold.level.aggressive'
                            )}
                          </Radio>
                          <Text
                            fontSize="sm"
                            lineHeight="sm"
                            color={colorStyle.color.secondary}
                            ml="6x"
                          >
                            {t(
                              'globalSetting.highProfileUsers.detectionThreshold.level.aggressive.desc'
                            )}
                          </Text>
                        </Box>
                        <Box>
                          <Radio value={String(ThresholdLevel.Normal)}>
                            {t(
                              'globalSetting.highProfileUsers.detectionThreshold.level.normal'
                            )}
                          </Radio>
                          <Text
                            fontSize="sm"
                            lineHeight="sm"
                            color={colorStyle.color.secondary}
                            ml="6x"
                          >
                            {t(
                              'globalSetting.highProfileUsers.detectionThreshold.level.normal.desc'
                            )}
                          </Text>
                        </Box>
                        <Box>
                          <Radio value={String(ThresholdLevel.Conservative)}>
                            {t(
                              'globalSetting.highProfileUsers.detectionThreshold.level.conservative'
                            )}
                          </Radio>
                          <Text
                            fontSize="sm"
                            lineHeight="sm"
                            color={colorStyle.color.secondary}
                            ml="6x"
                          >
                            {t(
                              'globalSetting.highProfileUsers.detectionThreshold.level.conservative.desc'
                            )}
                          </Text>
                        </Box>
                      </Stack>
                    </RadioGroup>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            minWidth="20x"
            onClick={handleSave}
            disabled={disabled || saving || loading}
          >
            {saving ? '...' : t('common.button.save')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={handleCancel} disabled={saving}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DetectionThresholdModal;
